// useSyncExternalStore is currently unused — see the DISABLED note near MemoizedChartViz.
import React, { useRef, useState, useCallback, useEffect, useMemo, memo } from 'react';
import isEqual from 'lodash/isEqual';
import { ConfigProvider, Checkbox } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import {
    AgenticRightPanelProps,
    AgenticAccordionItem,
    AgenticChildAccordionItem,
    AnalysisSummaryContent,
    ChartVisualizationContent,
    RightPanelBlock,
    AgenticExportDeepLinkConfig,
    DislikeFeedbackSubmitPayload,
} from '../AskAsperAgentic.types';
import type { AskAsperAccordionMenuItem } from '../../../molecules/AskAsperAccordion/AskAsperAccordionMenu';
import DOCX from '../../../atoms/Icon/DOCX';
import PDF from '../../../atoms/Icon/PDF';
import PPT from '../../../atoms/Icon/PPT';
import AgenticArtifactCard from './AgenticArtifactCard';
import AgenticAnalysisSummary from './AgenticAnalysisSummary';
import AgenticAnalysisSummaryLoading from './AgenticAnalysisSummaryLoading';
import CheckDone from '../../../atoms/Icon/CheckDone';
import Menu from '../../../atoms/Icon/Menu';
import Stand from '../../../atoms/Icon/Stand';
import XClose from '../../../atoms/Icon/XClose';
import DownloadFile from '../../../atoms/Icon/DownloadFile';
import AskAsperAccordion from '../../../molecules/AskAsperAccordion/AskAsperAccordion';
import KeyFindings from '../../../molecules/KeyFindings/KeyFindings';
import KeyTakeaways from '../../../molecules/KeyTakeaways/KeyTakeaways';
import { agenticMarkdownComponents } from '../../../shared/markdownComponents';
import DataSourceTables from '../../../molecules/ViewMethodology/DataSourceTables';
import MethodologyWithExportCapture from '../shared/MethodologyWithExportCapture';
import Tooltip from '../../../atoms/Tooltip/Tooltip';
import EnhancedBarChartV2 from '../../../molecules/EnhancedBarChart/EnhancedBarChartV2';
import EnhancedHorizontalBarChart from '../../../molecules/EnhancedHorizontalBarChart/EnhancedHorizontalBarChart';
import EnhancedLineChartV2 from '../../../molecules/EnhancedLineChart/EnhancedLineChartV2';
import EnhancedStackedBarChartV2 from '../../../molecules/EnhancedStackedBarChart/EnhancedStackedBarChartV2';
import DataTable, { VizDataFormat } from '../../../molecules/DataTable/DataTable';
import {
    DATA_TABLE_MAX_PREVIEW_BODY_ROWS,
    getTablePreviewStats,
} from '../../../molecules/DataTable/tablePreviewStats';
import TableExportPdfMetadata from '../../../molecules/DataTable/TableExportPdfMetadata';
import BrandPriceLadder from '../../../molecules/BrandPriceLadder/BrandPriceLadder';
import WaterfallChart from '../../../molecules/WaterfallChart/WaterfallChart';
import { VizData, ChartInteractionEventCallback } from '../../../molecules/EnhancedBarChart/EnhancedBarChart.types';
import { BrandPriceLadderVizData } from '../../../molecules/BrandPriceLadder/BrandPriceLadder.types';
import { isVizDataErrorState, isVizDataLoadingState } from '../../../../utils/transformVizData';
import {
    renderEChartsHtmlIfPresent,
    type AgenticEChartsChartRender,
} from '../../../shared/EChartsHtmlChart';
import {
    exportContainerToPDF,
    exportContainerToDOCX,
    exportContainerToPPTX,
    exportMultipleElementsToPDF,
    exportMultipleElementsToDOCX,
    exportMultipleElementsToPPTX,
} from '../../../../utils/artifactExport';
import { buildAskAsperExportBasename } from '../../../../utils/exportFilename';
import {
    // Kept imported but unused for now — see the DISABLED note near MemoizedChartViz's
    // `htmlChart` computation for why the ECharts→Recharts export swap was reverted.
    // subscribeChartExportMode,
    // getChartExportModeSnapshot,
    withChartExportMode,
} from '../../../../utils/chartExportMode';
import AskAsperAccordionMenu from '../../../molecules/AskAsperAccordion/AskAsperAccordionMenu';
import useNotification from '../../../atoms/Notification/useNotification';
import Excel from '../../../atoms/Icon/Excel';
import {
    openAgenticExportProgressNotification,
    awaitMinimumDurationThenCloseAgenticExportProgress,
    awaitProgressNotificationPaint,
} from '../utils/agenticExportProgressNotification';
import {
    extractTableVizFromChartVisualizationContent,
    exportAgenticTableVizToExcel,
    exportMultipleAgenticTableVizToExcel,
    type ExportTableVizSheetSpec,
} from '../utils/exportAgenticTableVizToExcel';
import { buildAgenticExportDeepLinkUrl, accordionShouldForceExpandForDeepLink, scrollAgenticRightPanelToTask, scrollAgenticRightPanelToRun } from '../utils/buildAgenticExportDeepLink';
import { formatTaskTitleWithSerial } from '../utils/taskSerialTitle';

function toAccordionDislikeFeedbackSubmit(
    handler?: (payload: DislikeFeedbackSubmitPayload) => void,
): ((payload: DislikeFeedbackSubmitPayload) => void) | undefined {
    return handler;
}

/** Context for deep links appended below tables during PDF export. */
interface ExportDeepLinkContext {
    config?: AgenticExportDeepLinkConfig;
    threadId?: string;
    taskId?: string;
}

/**
 * Memoized wrapper for individual chart/table visualizations.
 * Prevents re-rendering when the parent re-renders due to unrelated SSE events
 * (e.g. intermediate_step, ai) that don't change the chart data.
 */
// const MemoizedViz = React.memo<{
//     vizData: any;
//     chartType: string;
//     vizIndex: number;
//     dataTestId?: string;
// }>(
//     ({ vizData, chartType, vizIndex, dataTestId }) => {
//         const normalizedChartType = String(chartType || vizData?.chart_type || 'line').toLowerCase();

//         if (normalizedChartType === 'waterfall') {
//             return (
//                 <WaterfallChart
//                     config={{ viz_data: vizData }}
//                     data-test-id={`${dataTestId}-waterfall-chart-${vizIndex}`}
//                 />
//             );
//         }
//         if (normalizedChartType === 'stackbar') {
//             const transformed = transformVizDataToHorizontalStackBarChart(vizData);
//             if (!transformed.data.length) return null;
//             return (
//                 <HorizontalStackBarChart
//                     data={transformed.data}
//                     barsConfig={transformed.barsConfig as any}
//                     legend={transformed.legend}
//                     title={(vizData as any)?.chartTitle}
//                     data-test-id={`${dataTestId}-stackbar-chart-${vizIndex}`}
//                 />
//             );
//         }
//         if (normalizedChartType === 'bar') {
//             return (
//                 <EnhancedBarChart
//                     vizData={vizData as VizData}
//                     onBarClick={() => {}}
//                     data-test-id={`${dataTestId}-bar-chart-${vizIndex}`}
//                 />
//             );
//         }
//         if (normalizedChartType === 'line') {
//             return (
//                 <EnhancedLineChart
//                     vizData={vizData as VizData}
//                     onPointClick={() => {}}
//                     data-test-id={`${dataTestId}-line-chart-${vizIndex}`}
//                 />
//             );
//         }
//         if (normalizedChartType === 'table') {
//             return (
//                 <DataTable
//                     vizData={vizData as VizDataFormat}
//                     data-test-id={`${dataTestId}-data-table-${vizIndex}`}
//                 />
//             );
//         }
//         if (normalizedChartType === 'bpl') {
//             return (
//                 <BrandPriceLadder
//                     vizData={vizData as BrandPriceLadderVizData}
//                     data-test-id={`${dataTestId}-bpl-chart-${vizIndex}`}
//                 />
//             );
//         }
//         return null;
//     },
//     (prevProps, nextProps) => {
//         if (prevProps.chartType !== nextProps.chartType) return false;
//         if (prevProps.vizIndex !== nextProps.vizIndex) return false;
//         if (prevProps.dataTestId !== nextProps.dataTestId) return false;
//         if (prevProps.vizData === nextProps.vizData) return true;
//         try {
//             return JSON.stringify(prevProps.vizData) === JSON.stringify(nextProps.vizData);
//         } catch {
//             return false;
//         }
//     },
// );

/**
 * Renders the content for an Analysis Summary accordion
 */
const renderAnalysisSummaryContent = (
    content: AnalysisSummaryContent,
    dataTestId?: string,
    onCitationClick?: (taskId: string, serial: number) => void,
) => {
    const hasKeyTakeaways = content.keyTakeaways && content.keyTakeaways.items.length > 0;
    if (!hasKeyTakeaways) return null;

    return (
        <div className="flex flex-col gap-4 px-4">
            {/* Key Takeaways - data from key_takeaways, "Key Findings" is the section title */}
            {hasKeyTakeaways && (
                <KeyFindings
                    title={content.keyTakeaways!.title}
                    items={content.keyTakeaways!.items}
                    renderAsMarkdown
                    onCitationClick={onCitationClick}
                    data-test-id={`${dataTestId}-key-takeaways`}
                />
            )}
        </div>
    );
};

/**
 * Memoized chart/table component - re-renders only when vizData or chartType changes.
 * Prevents expensive chart re-renders when new content appears elsewhere in the right panel.
 */
interface MemoizedChartVizProps {
    vizData: any;
    chartType: string;
    vizIndex: number;
    dataTestId?: string;
    /** Generic chart interaction callback (slider/scroll/filter). Forwarded to the underlying
     *  Enhanced* chart via its `onTrackChartEvent` prop. */
    onTrackChartEvent?: ChartInteractionEventCallback;
    exportDeepLinkContext?: ExportDeepLinkContext;
    html?: string;
    render?: AgenticEChartsChartRender;
}

/** Skeleton loading placeholder for chart/table visualizations */
const ChartSkeleton: React.FC<{ isTable?: boolean; dataTestId?: string }> = ({ isTable, dataTestId }) => (
    <div
        className="bg-white rounded-lg border border-[rgba(0,0,0,0.06)] shadow-sm p-6 animate-pulse"
        data-test-id={dataTestId}
        data-chart-skeleton
    >
        <div className="flex items-center justify-between mb-4">
            <div className="h-5 w-32 bg-[#e5e7eb] rounded" />
            <span className="inline-flex px-2.5 py-0.5 rounded-full text-sm text-[#71717a] bg-[#f4f4f5]">
                In Progress
            </span>
        </div>
        {isTable ? (
            <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-10 bg-[#e5e7eb] rounded" style={{ width: `${100 - i * 5}%` }} />
                ))}
            </div>
        ) : (
            <div className="h-64 bg-[#e5e7eb] rounded" />
        )}
    </div>
);

const MemoizedChartViz = memo(
    ({ vizData, chartType, vizIndex, dataTestId, onTrackChartEvent, exportDeepLinkContext, html, render }: MemoizedChartVizProps) => {
        // DISABLED (do not remove — revisit later, see note below near `htmlChart`).
        // Subscribes to the shared export-mode flag (see chartExportMode.ts) so that,
        // once re-enabled, an export in flight can swap the ECharts iframe for a
        // capturable Recharts fallback.
        // const isExportMode = useSyncExternalStore(subscribeChartExportMode, getChartExportModeSnapshot);

        if (isVizDataErrorState(vizData)) return null;
        if (isVizDataLoadingState(vizData)) {
            const isTable = String(vizData?.chart_type ?? '').toLowerCase() === 'table';
            return <ChartSkeleton isTable={isTable} dataTestId={`${dataTestId}-chart-skeleton-${vizIndex}`} />;
        }
        const normalizedChartType = String(chartType || vizData?.chart_type || 'line').toLowerCase();
        // Tables carry the same rows/columns in vizData.data as the html iframe embeds - prefer the
        // native DataTable render so PDF/DOCX/PPT export can capture it (html2canvas cannot capture
        // content inside a sandboxed iframe, which would otherwise export as a blank table).
        const hasNativeTableData =
            normalizedChartType === 'table' && Array.isArray((vizData as VizDataFormat | undefined)?.data);
        // DISABLED (do not remove — revisit later): during export, we tried skipping the
        // ECharts iframe and falling through to the Recharts branches below so html2canvas
        // could capture *something*. That works for chart types with a real dedicated
        // Recharts component (bar/line/stackbar/waterfall/bpl/table), but 'horizontalbar' has
        // no faithful equivalent — it's a generic multi-metric component (EnhancedBarChartV2
        // with a `horizontal` flag), so swapping it in rendered the WRONG chart (different
        // filter UI, different default selection/sort) for any ECharts visualization shaped
        // like a horizontal bar. Reverted until we either (a) gate the swap to only the chart
        // types with a trustworthy match, or (b) build a real component / server-side render
        // for this chart type. To re-enable the swap for all types, restore:
        //   hasNativeTableData || isExportMode
        // in place of `hasNativeTableData` below.
        const htmlChart = hasNativeTableData
            ? null
            : renderEChartsHtmlIfPresent({ html, vizData, render }, `${dataTestId}-echarts-html-${vizIndex}`);
        if (htmlChart) return htmlChart;

        if (normalizedChartType === 'waterfall') {
            return (
                <WaterfallChart
                    config={{ viz_data: vizData }}
                    showConnectors={false}
                    data-test-id={`${dataTestId}-waterfall-chart-${vizIndex}`}
                />
            );
        }
        if (normalizedChartType === 'stackbar') {
            return (
                <EnhancedStackedBarChartV2
                    vizData={vizData as any}
                    onBarClick={() => {}}
                    onTrackChartEvent={onTrackChartEvent}
                    data-test-id={`${dataTestId}-stackbar-chart-${vizIndex}`}
                />
            );
        }
        if (normalizedChartType === 'horizontalbar') {
            return (
                <EnhancedHorizontalBarChart
                    vizData={vizData as VizData}
                    onBarClick={() => {}}
                    onTrackChartEvent={onTrackChartEvent}
                    data-test-id={`${dataTestId}-horizontal-bar-chart-${vizIndex}`}
                />
            );
        }
        if (normalizedChartType === 'bar') {
            return (
                <EnhancedBarChartV2
                    vizData={vizData as VizData}
                    onBarClick={() => {}}
                    onTrackChartEvent={onTrackChartEvent}
                    data-test-id={`${dataTestId}-bar-chart-${vizIndex}`}
                />
            );
        }
        if (normalizedChartType === 'line') {
            return (
                <EnhancedLineChartV2
                    vizData={vizData as VizData}
                    onPointClick={() => {}}
                    onTrackChartEvent={onTrackChartEvent}
                    data-test-id={`${dataTestId}-line-chart-${vizIndex}`}
                />
            );
        }
        if (normalizedChartType === 'table') {
            const deepLinkUrl =
                exportDeepLinkContext?.config &&
                exportDeepLinkContext.threadId &&
                exportDeepLinkContext.taskId
                    ? buildAgenticExportDeepLinkUrl(
                          exportDeepLinkContext.config,
                          exportDeepLinkContext.threadId,
                          { taskId: exportDeepLinkContext.taskId },
                      )
                    : undefined;
            const previewStats = getTablePreviewStats(
                vizData as VizDataFormat,
                DATA_TABLE_MAX_PREVIEW_BODY_ROWS,
            );

            return (
                <>
                    <DataTable
                        vizData={vizData as VizDataFormat}
                        maxWidth="100%"
                        maxScrollBodyRows={DATA_TABLE_MAX_PREVIEW_BODY_ROWS}
                        data-test-id={`${dataTestId}-data-table-${vizIndex}`}
                    />
                    {previewStats && (
                        <TableExportPdfMetadata
                            previewStats={previewStats}
                            deepLinkUrl={deepLinkUrl}
                            data-test-id={`${dataTestId}-table-export-pdf-meta-${vizIndex}`}
                        />
                    )}
                </>
            );
        }
        if (normalizedChartType === 'bpl') {
            return (
                <BrandPriceLadder
                    vizData={vizData as BrandPriceLadderVizData}
                    data-test-id={`${dataTestId}-bpl-chart-${vizIndex}`}
                />
            );
        }
        return null;
    },
    (prev, next) =>
        prev.vizIndex === next.vizIndex &&
        prev.chartType === next.chartType &&
        prev.html === next.html &&
        prev.render?.height === next.render?.height &&
        prev.render?.sandbox === next.render?.sandbox &&
        isEqual(prev.vizData, next.vizData) &&
        prev.dataTestId === next.dataTestId &&
        prev.onTrackChartEvent === next.onTrackChartEvent &&
        prev.exportDeepLinkContext?.config === next.exportDeepLinkContext?.config &&
        prev.exportDeepLinkContext?.threadId === next.exportDeepLinkContext?.threadId &&
        prev.exportDeepLinkContext?.taskId === next.exportDeepLinkContext?.taskId,
);

MemoizedChartViz.displayName = 'MemoizedChartViz';

/**
 * Renders the content for a Chart Visualization accordion
 * Supports multiple visualizations per task (chart + table) in the same card
 */
const renderChartVisualizationContent = (
    content: ChartVisualizationContent,
    dataTestId?: string,
    onMethodologyExpandChange?: (expanded: boolean, artefactTitle?: string) => void,
    artefactTitle?: string,
    onTrackChartEvent?: ChartInteractionEventCallback,
    exportDeepLinkContext?: ExportDeepLinkContext,
) => {
    const renderChart = () => {
        if (content.customContent) {
            return content.customContent;
        }

        if (content.visualizations && content.visualizations.length > 0) {
            // Deduplicate visualizations by vizData to prevent rendering the same visualization twice during streaming
            const uniqueVisualizations = Array.from(
                new Map(
                    content.visualizations.map((item) => [
                        JSON.stringify({ vizData: item.vizData, chartType: item.chartType }),
                        item,
                    ]),
                ).values(),
            );

            // Track which links have been shown across all visualizations to avoid duplicates
            const shownLinkUrls = new Set<string>();

            return (
                <div className="flex flex-col gap-6 min-w-0">
                    {uniqueVisualizations.map((item, idx) => {
                        // Filter links to only show each unique link once across all visualizations
                        const uniqueLinksForThisViz = item.scenarioLinks
                            ? Array.from(new Map(item.scenarioLinks.map((link) => [link.url, link])).values()).filter((link) => {
                                  if (shownLinkUrls.has(link.url)) {
                                      return false; // Skip if already shown
                                  }
                                  shownLinkUrls.add(link.url); // Mark as shown
                                  return true;
                              })
                            : [];

                        return (
                            <div
                                key={`viz-${idx}`}
                                data-export-slide
                                data-export-slide-title={(item.vizData as any)?.chartTitle || `Visualization ${idx + 1}`}
                                className="w-full min-w-0 flex flex-col gap-2"
                            >
                                <MemoizedChartViz
                                    vizData={item.vizData}
                                    chartType={item.chartType ?? ''}
                                    vizIndex={idx}
                                    dataTestId={dataTestId}
                                    onTrackChartEvent={onTrackChartEvent}
                                    exportDeepLinkContext={exportDeepLinkContext}
                                    html={item.html}
                                    render={item.render}
                                />
                                {uniqueLinksForThisViz.length > 0 ? (
                                    <div className="flex flex-col gap-1" data-export-exclude>
                                        {uniqueLinksForThisViz.map((link, linkIdx) => (
                                            <div key={link.scenario_id ?? linkIdx} className="flex items-center gap-1">
                                                <div className="font-['Open_Sans'] text-sm">
                                                    To explore this scenario, open the
                                                </div>
                                                <a
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-['Open_Sans'] text-sm text-[#5f49bc] hover:underline"
                                                    data-test-id={`${dataTestId}-scenario-link-${idx}-${linkIdx}`}
                                                >
                                                    Holistic Pricing Simulator
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    item.scenarioLink &&
                                    !shownLinkUrls.has(item.scenarioLink) && (
                                        <div className="flex items-center gap-1" data-export-exclude>
                                            <div className="font-['Open_Sans'] text-sm">
                                                For surgical price increases at PPG/SKU level, open this simulation in the
                                            </div>
                                            <a
                                                href={item.scenarioLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-['Open_Sans'] text-sm text-[#5f49bc] hover:underline"
                                                data-test-id={`${dataTestId}-scenario-link-${idx}`}
                                            >
                                                Holistic Pricing Simulator
                                            </a>
                                        </div>
                                    )
                                )}
                                {/* Screen-reader-only mirror of the scenario link(s), excluded from
                                    html2canvas capture and appended as native, clickable PDF text
                                    by artifactExport.ts instead - avoids visual clipping / a dead
                                    (rasterized) link in exported PDFs. */}
                                {(() => {
                                    const exportScenarioLinkUrls =
                                        uniqueLinksForThisViz.length > 0
                                            ? uniqueLinksForThisViz.map((link) => link.url)
                                            : item.scenarioLink
                                              ? [item.scenarioLink]
                                              : [];
                                    if (exportScenarioLinkUrls.length === 0) return null;
                                    return (
                                        <span
                                            data-export-exclude
                                            data-export-scenario-links={JSON.stringify(exportScenarioLinkUrls)}
                                            className="sr-only"
                                            aria-hidden
                                        />
                                    );
                                })()}
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (content.vizData || content.html) {
            const chartType = content.chartType || content.vizData?.chart_type?.toLowerCase() || 'line';
            return (
                <div
                    data-export-slide
                    data-export-slide-title={(content.vizData as any)?.chartTitle || 'Visualization'}
                    className="min-w-0"
                >
                    <MemoizedChartViz
                        vizData={content.vizData}
                        chartType={chartType}
                        vizIndex={0}
                        dataTestId={dataTestId}
                        onTrackChartEvent={onTrackChartEvent}
                        exportDeepLinkContext={exportDeepLinkContext}
                        html={content.html}
                        render={content.render}
                    />
                </div>
            );
        }

        return null;
    };

    const hasViz =
        content.customContent ||
        content.vizData ||
        content.html ||
        (content.visualizations?.length ?? 0) > 0;

    return (
        <div className="flex flex-col gap-4 px-4">
            {/* Key Takeaways */}
            {content.keyTakeaways && content.keyTakeaways.items.length > 0 && (
                <div data-export-slide data-export-slide-title={content.keyTakeaways.title || 'Key Takeaways'}>
                    <KeyTakeaways
                        title={content.keyTakeaways.title}
                        items={content.keyTakeaways.items}
                        renderAsMarkdown
                        customMarkdownComponents={agenticMarkdownComponents}
                        data-test-id={`${dataTestId}-key-takeaways`}
                    />
                </div>
            )}

            {/* Chart/Table visualizations (max 2 per task: chart + table) */}
            {hasViz && <div className="w-full min-w-0">{renderChart()}</div>}

            {/* Legacy: scenario link(s) when content.scenarioLink(s) (no per-viz links) */}
            {!content.visualizations?.length && content.scenarioLinks && content.scenarioLinks.length > 0 ? (
                <div className="flex flex-col gap-1">
                    {/* Deduplicate scenarioLinks by URL to prevent showing duplicate links */}
                    {Array.from(new Map(content.scenarioLinks.map((link) => [link.url, link])).values()).map(
                        (link, linkIdx) => (
                            <div key={link.scenario_id ?? linkIdx} className="flex items-center gap-1">
                                <div className="font-['Open_Sans'] text-sm">To explore this scenario, open the</div>
                                <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-['Open_Sans'] text-sm text-[#5f49bc] hover:underline"
                                    data-test-id={`${dataTestId}-scenario-link-${linkIdx}`}
                                >
                                    Holistic Pricing Simulator
                                </a>
                            </div>
                        ),
                    )}
                </div>
            ) : (
                !content.visualizations?.length &&
                content.scenarioLink && (
                    <div className="flex items-center gap-1">
                        <div className="font-['Open_Sans'] text-sm">
                            For surgical price increases at PPG/SKU level, open this simulation in the
                        </div>
                        <a
                            href={content.scenarioLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-['Open_Sans'] text-sm text-[#5f49bc] hover:underline"
                            data-test-id={`${dataTestId}-scenario-link`}
                        >
                            Holistic Pricing Simulator
                        </a>
                    </div>
                )
            )}

            {/* Methodology (assumptions as bullets; formulae in shaded box; SQL query when present) */}
            {content.methodology &&
                (content.methodology.sections.length > 0 ||
                    content.methodology.formula ||
                    content.methodology.sqlQuery ||
                    content.methodology.tableSamples?.length) && (
                <Tooltip
                    title="See how this result was calculated"
                    placement="top"
                    getPopupContainer={() => document.body}
                >
                    <div className="block w-full">
                        <MethodologyWithExportCapture
                            title={content.methodology.title}
                            sections={content.methodology.sections}
                            formula={content.methodology.formula}
                            sqlQuery={content.methodology.sqlQuery}
                            dataSourceContent={<DataSourceTables samples={content.methodology.tableSamples ?? []} />}
                            defaultExpanded={false}
                            onExpandChange={
                                onMethodologyExpandChange
                                    ? (expanded) =>
                                          onMethodologyExpandChange(
                                              expanded,
                                              artefactTitle ?? content.methodology?.title,
                                          )
                                    : undefined
                            }
                            data-test-id={`${dataTestId}-methodology`}
                        />
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

/** Returns true if Analysis Summary content has renderable key takeaways */
const hasAnalysisSummaryContent = (content: AnalysisSummaryContent): boolean => !!content.keyTakeaways?.items?.length;

/**
 * Renders accordion content based on type
 */
const renderAccordionContent = (
    content: AnalysisSummaryContent | ChartVisualizationContent | undefined,
    dataTestId?: string,
    onMethodologyExpandChange?: (expanded: boolean, artefactTitle?: string) => void,
    artefactTitle?: string,
    onTrackChartEvent?: ChartInteractionEventCallback,
    exportDeepLinkContext?: ExportDeepLinkContext,
    onCitationClick?: (taskId: string, serial: number) => void,
) => {
    if (!content) return null;

    if (content.type === 'analysisSummary') {
        return renderAnalysisSummaryContent(content, dataTestId, onCitationClick);
    }

    if (content.type === 'chartVisualization') {
        return renderChartVisualizationContent(
            content,
            dataTestId,
            onMethodologyExpandChange,
            artefactTitle,
            onTrackChartEvent,
            exportDeepLinkContext,
        );
    }

    return null;
};

function getChildArtifactSelectionState(
    children: AgenticChildAccordionItem[],
    selectedArtifactIds: string[],
): { allSelected: boolean; someSelected: boolean } {
    const childIds = children.map((c) => c.id);
    if (childIds.length === 0) {
        return { allSelected: false, someSelected: false };
    }
    const selectedCount = childIds.filter((id) => selectedArtifactIds.includes(id)).length;
    return {
        allSelected: selectedCount === childIds.length,
        someSelected: selectedCount > 0 && selectedCount < childIds.length,
    };
}

/**
 * Parent Accordion Header for group accordions
 * Renders a simpler header with numbered title, divider line, and chevron
 */
interface ParentAccordionProps {
    title: string;
    index?: number;
    defaultExpanded?: boolean;
    /** When set, controls expand/collapse (e.g. deep-link navigation). */
    expanded?: boolean;
    children: React.ReactNode;
    dataTestId?: string;
    /** When true, shows a checkbox before the question title to select/deselect all child artifacts */
    showCheckbox?: boolean;
    bulkChecked?: boolean;
    bulkIndeterminate?: boolean;
    onBulkCheckChange?: (checked: boolean) => void;
}

const ParentAccordion: React.FC<ParentAccordionProps> = ({
    title,
    index,
    defaultExpanded = true,
    expanded: controlledExpanded,
    children,
    dataTestId,
    showCheckbox = false,
    bulkChecked = false,
    bulkIndeterminate = false,
    onBulkCheckChange,
}) => {
    const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
    const isControlled = controlledExpanded !== undefined;
    const isExpanded = isControlled ? controlledExpanded : internalExpanded;

    return (
        <div className="flex flex-col gap-4" data-test-id={dataTestId}>
            {/* Parent Accordion Header */}
            <div className="flex items-center w-full gap-3 min-w-0">
                {showCheckbox && (
                    <Checkbox
                        checked={bulkChecked}
                        indeterminate={bulkIndeterminate}
                        onChange={(e) => onBulkCheckChange?.(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 [&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-[#5f49bc] [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-[#5f49bc]"
                        data-test-id={dataTestId ? `${dataTestId}-question-bulk-checkbox` : undefined}
                    />
                )}
                <button
                    type="button"
                    className="flex items-center justify-between flex-1 min-w-0 bg-transparent border-none cursor-pointer p-0"
                    onClick={() => {
                        if (!isControlled) {
                            setInternalExpanded(!isExpanded);
                        }
                    }}
                    aria-expanded={isExpanded}
                >
                    {/* Left: Numbered title */}
                    <span
                        className="font-['Open_Sans'] text-[16px] leading-normal text-[#140b1d] whitespace-normal break-words [overflow-wrap:anywhere] min-w-0 text-left"
                        style={{ fontWeight: 600 }}
                    >
                        {index !== undefined ? `${index}. ` : ''}
                        {title}
                    </span>

                    {/* Middle: Divider line */}
                    <div className="flex-1 h-px bg-[rgba(0,0,0,0.06)] mx-4" />

                    {/* Right: Chevron */}
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        className={`transition-transform shrink-0 ${isExpanded ? '' : 'rotate-180'}`}
                    >
                        <path
                            d="M18 15L12 9L6 15"
                            stroke="#71717A"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>

            {/* Children - nested accordions */}
            {isExpanded && <div className="flex flex-col gap-4 pl-0">{children}</div>}
        </div>
    );
};

/**
 * Renders a child accordion item inside a parent group
 */
const renderChildAccordionItem = (
    item: AgenticChildAccordionItem,
    isMultiSelectMode: boolean,
    selectedArtifactIds: string[],
    onArtifactSelectionChange?: (artifactId: string, selected: boolean) => void,
    dataTestId?: string,
    getMenuItems?: (item: AgenticChildAccordionItem) => AskAsperAccordionMenuItem[],
    /** Group header title: used as disclaimer context for the Summary child when `dislikeFeedbackContextTitle` is unset */
    parentGroupTitle?: string,
    onMethodologyExpandChange?: (expanded: boolean, artefactTitle?: string) => void,
    onArtefactMenuTriggerClick?: (artefactTitle: string) => void,
    onTrackChartEvent?: ChartInteractionEventCallback,
    exportDeepLinkContext?: ExportDeepLinkContext,
    deepLinkTaskId?: string,
    onCitationClick?: (taskId: string, serial: number) => void,
) => {
    const menuItems = item.menuItems ?? getMenuItems?.(item);
    const isSummaryChild = item.content?.type === 'analysisSummary';
    const dislikeContextTitle =
        item.dislikeFeedbackContextTitle ?? (isSummaryChild && parentGroupTitle ? parentGroupTitle : undefined);
    const forceExpandForDeepLink = deepLinkTaskId === item.id;

    return (
        <div key={item.id} data-task-id={item.id} data-export-slide-title={item.title} className="w-full">
            <AskAsperAccordion
                title={formatTaskTitleWithSerial(item.title, item.taskSerial)}
                defaultExpanded={item.defaultExpanded ?? false}
                expanded={forceExpandForDeepLink ? true : undefined}
                showCheckbox={isMultiSelectMode}
                checked={selectedArtifactIds.includes(item.id)}
                onCheckChange={(isChecked: boolean) => onArtifactSelectionChange?.(item.id, isChecked)}
                initialFeedbackVote={item.initialFeedbackVote}
                onThumbsDown={item.onThumbsDown}
                onThumbsUp={item.onThumbsUp}
                onDislikeFeedbackSubmit={toAccordionDislikeFeedbackSubmit(item.onDislikeFeedbackSubmit)}
                dislikeFeedbackContextTitle={dislikeContextTitle}
                menuItems={menuItems}
                menuTriggerIcon="export"
                onMenuTriggerClick={
                    onArtefactMenuTriggerClick ? () => onArtefactMenuTriggerClick(item.title) : undefined
                }
                data-test-id={`${dataTestId}-child-accordion-${item.id}`}
            >
                <div data-export-content className="w-full">
                    {item.subtitle && (
                        <p className="px-4 pb-2 font-['Open_Sans'] text-[14px] text-[rgba(0,0,0,0.65)] leading-normal">
                            {item.subtitle}
                        </p>
                    )}
                    {renderAccordionContent(
                        item.content,
                        `${dataTestId}-child-accordion-${item.id}`,
                        onMethodologyExpandChange,
                        item.title,
                        onTrackChartEvent,
                        exportDeepLinkContext
                            ? { ...exportDeepLinkContext, taskId: item.id }
                            : undefined,
                        onCitationClick,
                    )}
                </div>
            </AskAsperAccordion>
        </div>
    );
};

/**
 * AgenticRightPanel - Right panel component for artifacts and summary
 *
 * This component displays:
 * - Accordion-based content (Analysis Summary, Chart Visualizations)
 * - Visualization artifacts (charts, tables, heatmaps)
 * - Overall conversation summary
 *
 * The panel is designed to be responsive and scrollable
 */
const EMPTY_ACCORDION_ITEMS: AgenticRightPanelProps['accordionItems'] = [];
const EMPTY_ARTIFACTS: AgenticRightPanelProps['artifacts'] = [];
const EMPTY_SELECTED_IDS: string[] = [];

function findAgenticItemById(
    items: AgenticAccordionItem[],
    id: string,
): AgenticAccordionItem | AgenticChildAccordionItem | null {
    for (const item of items) {
        if (item.id === id) {
            return item;
        }
        if (item.children?.length) {
            for (const child of item.children) {
                if (child.id === id) {
                    return child;
                }
            }
        }
    }
    return null;
}

/**
 * Full Excel export is keyed only by (session_id / thread_id, run_id). Multiple selected artefacts
 * from the same assistant message share those ids — one API call per distinct pair.
 * Only artefacts that render a {@link DataTable} (table chart type with data) are included so the
 * export API is not invoked for chart-only or summary selections.
 */
function collectUniqueRunSessionPairsForExport(
    items: AgenticAccordionItem[],
    selectedArtifactIds: string[],
): Array<{ run_id: string; session_id: string }> {
    const seen = new Set<string>();
    const pairs: Array<{ run_id: string; session_id: string }> = [];
    for (const id of selectedArtifactIds) {
        const found = findAgenticItemById(items, id);
        if (!found) continue;
        if (found.content?.type !== 'chartVisualization') continue;
        if (extractTableVizFromChartVisualizationContent(found.content) == null) continue;
        const run_id = found.run_id?.trim();
        const session_id = found.session_id?.trim();
        if (!run_id || !session_id) continue;
        const key = `${session_id}\0${run_id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        pairs.push({ run_id, session_id });
    }
    return pairs;
}

function filterSelectedArtifactIdsWithDataTable(
    items: AgenticAccordionItem[],
    selectedArtifactIds: string[],
): string[] {
    return selectedArtifactIds.filter((id) => {
        const found = findAgenticItemById(items, id);
        if (!found?.content || found.content.type !== 'chartVisualization') {
            return false;
        }
        return extractTableVizFromChartVisualizationContent(found.content) != null;
    });
}

function blockContainsAccordionTaskId(items: AgenticAccordionItem[], taskId: string): boolean {
    for (const item of items) {
        if (item.id === taskId) return true;
        if (item.children?.some((c) => c.id === taskId)) return true;
    }
    return false;
}

/** User chat query for the message that owns this artefact (for PDF/Word export preamble). */
function findUserQueryForTaskId(blocks: RightPanelBlock[] | undefined, taskId: string): string | undefined {
    if (!blocks?.length) return undefined;
    for (const block of blocks) {
        if (blockContainsAccordionTaskId(block.accordionItems, taskId)) {
            return block.userQuery?.trim() || undefined;
        }
    }
    return undefined;
}

const AgenticRightPanel: React.FC<AgenticRightPanelProps> = ({
    rightPanelBlocks,
    accordionItems: accordionItemsProp = EMPTY_ACCORDION_ITEMS,
    artifacts = EMPTY_ARTIFACTS,
    activeArtifactId,
    summaries: summariesProp,
    summary: summaryProp,
    onArtifactSelect,
    onArtifactClose,
    isMultiSelectMode = false,
    onToggleMultiSelect,
    selectedArtifactIds = EMPTY_SELECTED_IDS,
    onArtifactSelectionChange,
    onSelectAll,
    onDeselectAll,
    onMethodologyExpandChange,
    onArtefactMenuTriggerClick,
    onArtefactExportOptionClick,
    onMultiSelectExportOptionClick,
    onTrackChartEvent,
    onClose,
    onArtifactExport,
    onMultiSelectExport,
    onCreateCase,
    showFocusModeToggleInPanel = false,
    viewMode = 'list',
    onViewModeChangeInPanel,
    conversationPairCount,
    conversationTitle,
    exportDeepLink,
    threadId,
    deepLinkTaskId,
    onDeepLinkTaskScrollComplete,
    deepLinkRunId,
    onDeepLinkRunScrollComplete,
    onSummaryCitationClick,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const {
        open: openNotification,
        success: showSuccessNotification,
        error: showErrorNotification,
    } = useNotification();
    const contentExportRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    /** When true, user has manually scrolled up - disable auto-scroll until next question */
    const userHasScrolledUpRef = useRef(false);
    /** Timestamp of last programmatic scroll - used to ignore our own scroll events */
    const lastProgrammaticScrollRef = useRef<number>(0);
    const prevPairCountRef = useRef<number>(conversationPairCount ?? 0);
    const prevContentScrollKeyRef = useRef('');
    const isProgrammaticScrollRef = useRef(false);
    const SCROLL_NEAR_BOTTOM_THRESHOLD = 80;
    const resolvedConversationTitle = conversationTitle?.trim() || 'Untitled Chat';

    const exportDeepLinkContext = useMemo<ExportDeepLinkContext | undefined>(() => {
        if (!exportDeepLink || !threadId) return undefined;
        return { config: exportDeepLink, threadId };
    }, [exportDeepLink, threadId]);

    const accordionItems = useMemo(
        () => (rightPanelBlocks != null ? rightPanelBlocks.flatMap((b) => b.accordionItems) : accordionItemsProp),
        [rightPanelBlocks, accordionItemsProp],
    );
    const summaries = useMemo(
        () =>
            rightPanelBlocks != null
                ? rightPanelBlocks.flatMap((b) => (b.summary ? [b.summary] : []))
                : (summariesProp ?? (summaryProp ? [summaryProp] : [])),
        [rightPanelBlocks, summariesProp, summaryProp],
    );

    const getTaskExportElement = useCallback((taskId: string): HTMLElement | null => {
        if (!contentExportRef.current) return null;
        const taskContainer = contentExportRef.current.querySelector<HTMLElement>(`[data-task-id="${taskId}"]`);
        if (!taskContainer) return null;
        return taskContainer.querySelector<HTMLElement>('[data-export-content]') ?? taskContainer;
    }, []);

    // PDF, Word, and PPT: frontend export via refs.
    // When taskId is provided, only the specific accordion element is exported.
    const handleExport = useCallback(
        async (format: 'docx' | 'pdf' | 'ppt', taskId?: string) => {
            openAgenticExportProgressNotification(openNotification, format, {
                'data-test-id': `${dataTestId}-export-progress`,
            });
            const exportProgressOpenedAtMs = performance.now();
            try {
                let exportTarget: HTMLElement | null = null;
                if (taskId) {
                    exportTarget = getTaskExportElement(taskId);
                }
                if (!exportTarget) {
                    exportTarget = contentExportRef.current;
                }

                const found = taskId ? findAgenticItemById(accordionItems, taskId) : null;
                const exportTitle = (found && found.title?.trim()) || resolvedConversationTitle;
                const exportUserQuery =
                    taskId && rightPanelBlocks != null ? findUserQueryForTaskId(rightPanelBlocks, taskId) : undefined;
                const ext = format === 'docx' ? 'docx' : format === 'pdf' ? 'pdf' : 'pptx';
                const explicitFilename =
                    taskId && found
                        ? `${buildAskAsperExportBasename(found.title || 'export')}.${ext}`
                        : undefined;

                // ECharts iframes can't be captured by html2canvas (sandboxed browsing
                // context) — swap them for the Recharts fallback for the duration of the
                // capture; see chartExportMode.ts for why this has to be a live re-render
                // rather than something done in html2canvas's onclone DOM clone.
                await withChartExportMode(async () => {
                    if (format === 'pdf') {
                        await exportContainerToPDF(exportTarget, {
                            title: exportTitle,
                            filename: explicitFilename,
                            userQuery: exportUserQuery,
                        });
                    } else if (format === 'docx') {
                        await exportContainerToDOCX(exportTarget, {
                            title: exportTitle,
                            filename: explicitFilename,
                            userQuery: exportUserQuery,
                        });
                    } else if (format === 'ppt') {
                        await exportContainerToPPTX(exportTarget, {
                            title: exportTitle,
                            filename: explicitFilename,
                            coverSlide: {
                                threadTitle: resolvedConversationTitle,
                                downloadedAt: new Date(),
                            },
                        });
                    }
                });
                showSuccessNotification({
                    message: 'Export successful',
                    description: `Artifact exported as ${format.toUpperCase()} successfully.`,
                });
            } catch (err) {
                showErrorNotification({
                    message: 'Export failed',
                    description: err instanceof Error ? err.message : 'Failed to export artifact.',
                });
            } finally {
                await awaitMinimumDurationThenCloseAgenticExportProgress(exportProgressOpenedAtMs);
            }
        },
        [
            accordionItems,
            getTaskExportElement,
            resolvedConversationTitle,
            rightPanelBlocks,
            showSuccessNotification,
            showErrorNotification,
            openNotification,
            dataTestId,
        ],
    );

    // Build menu items scoped to a specific accordion item - flat list so options appear directly (no intermediate "Export" popover)
    const getMenuItemsForItem = useCallback(
        (item: AgenticAccordionItem | AgenticChildAccordionItem): AskAsperAccordionMenuItem[] => {
            const tableVizForCurrentView =
                item.content?.type === 'chartVisualization'
                    ? extractTableVizFromChartVisualizationContent(item.content)
                    : null;

            const menu: AskAsperAccordionMenuItem[] = [
                {
                    key: 'export-word',
                    label: 'Export as Word',
                    icon: <DOCX size={16} />,
                    onClick: () => {
                        onArtefactExportOptionClick?.('docx', item.title);
                        handleExport('docx', item.id);
                    },
                },
                {
                    key: 'export-pdf',
                    label: 'Export as PDF',
                    icon: <PDF size={16} />,
                    onClick: () => {
                        onArtefactExportOptionClick?.('pdf', item.title);
                        handleExport('pdf', item.id);
                    },
                },
                {
                    key: 'export-ppt',
                    label: 'Export as PPT',
                    icon: <PPT size={16} />,
                    onClick: () => {
                        onArtefactExportOptionClick?.('ppt', item.title);
                        handleExport('ppt', item.id);
                    },
                },
                {
                    key: 'export-excel',
                    label: 'Export as Excel',
                    icon: <Excel size={16} />,
                    submenuPlacement: 'left',
                    children: [
                        {
                            key: 'export-excel-complete',
                            label: 'Export complete dataset',
                            onClick: () => {
                                onArtefactExportOptionClick?.('xlsx_complete_dataset', item.title);
                                void (async () => {
                                    openAgenticExportProgressNotification(openNotification, 'xlsx', {
                                        'data-test-id': `${dataTestId}-export-progress`,
                                    });
                                    const exportProgressOpenedAtMs = performance.now();
                                    try {
                                        await awaitProgressNotificationPaint();
                                        await Promise.resolve(onArtifactExport?.('xlsx', item.run_id, item.session_id));
                                        showSuccessNotification({
                                            message: 'Export successful',
                                            description: 'Complete dataset exported as Excel successfully.',
                                        });
                                    } catch (err) {
                                        showErrorNotification({
                                            message: 'Export failed',
                                            description:
                                                err instanceof Error ? err.message : 'Failed to export artifact.',
                                        });
                                    } finally {
                                        await awaitMinimumDurationThenCloseAgenticExportProgress(
                                            exportProgressOpenedAtMs,
                                        );
                                    }
                                })();
                            },
                        },
                        {
                            key: 'export-excel-current',
                            label: 'Export current view',
                            disabled: !tableVizForCurrentView,
                            onClick: () => {
                                if (!tableVizForCurrentView) {
                                    return;
                                }
                                onArtefactExportOptionClick?.('xlsx_current_view', item.title);
                                void (async () => {
                                    openAgenticExportProgressNotification(openNotification, 'xlsx', {
                                        'data-test-id': `${dataTestId}-export-progress`,
                                    });
                                    const exportProgressOpenedAtMs = performance.now();
                                    try {
                                        await awaitProgressNotificationPaint();
                                        exportAgenticTableVizToExcel(tableVizForCurrentView, item.title);
                                        showSuccessNotification({
                                            message: 'Export successful',
                                            description:
                                                'Current table view exported as Excel (data shown in this artefact).',
                                        });
                                    } catch (err) {
                                        showErrorNotification({
                                            message: 'Export failed',
                                            description:
                                                err instanceof Error
                                                    ? err.message
                                                    : 'Could not export the current table view.',
                                        });
                                    } finally {
                                        await awaitMinimumDurationThenCloseAgenticExportProgress(
                                            exportProgressOpenedAtMs,
                                        );
                                    }
                                })();
                            },
                        },
                    ],
                },
            ];
            if (item.content?.type === 'analysisSummary') {
                return menu.filter((m) => m.key !== 'export-excel');
            }
            return menu;
        },
        [
            handleExport,
            onArtifactExport,
            onArtefactExportOptionClick,
            showErrorNotification,
            showSuccessNotification,
            openNotification,
            dataTestId,
        ],
    );

    const handleMultiSelectExportCompleteDatasetXlsx = useCallback(async () => {
        if (selectedArtifactIds.length === 0) return;
        openAgenticExportProgressNotification(openNotification, 'xlsx', {
            'data-test-id': `${dataTestId}-export-progress`,
        });
        const exportProgressOpenedAtMs = performance.now();
        try {
            await awaitProgressNotificationPaint();
            const exportPairs = collectUniqueRunSessionPairsForExport(accordionItems, selectedArtifactIds);
            if (exportPairs.length === 0) {
                const tableBackedSelectedCount = filterSelectedArtifactIdsWithDataTable(
                    accordionItems,
                    selectedArtifactIds,
                ).length;
                showErrorNotification({
                    message: 'Export failed',
                    description:
                        tableBackedSelectedCount === 0
                            ? 'None of the selected artefacts include an exportable data table for complete dataset export.'
                            : 'Could not resolve thread and run for the selected artefacts with a data table. Each table artefact must belong to a response with export metadata.',
                });
                return;
            }
            if (onArtifactExport) {
                await Promise.all(
                    exportPairs.map((p) => Promise.resolve(onArtifactExport('xlsx', p.run_id, p.session_id))),
                );
            } else {
                const tableBackedIds = filterSelectedArtifactIdsWithDataTable(accordionItems, selectedArtifactIds);
                await Promise.resolve(onMultiSelectExport?.('xlsx', tableBackedIds));
            }
            showSuccessNotification({
                message: 'Export successful',
                description:
                    exportPairs.length === 1
                        ? 'Complete dataset exported as Excel successfully.'
                        : `Complete dataset exported as Excel successfully (${exportPairs.length} export(s)).`,
            });
        } catch (err) {
            showErrorNotification({
                message: 'Export failed',
                description: err instanceof Error ? err.message : 'Failed to export selected artifacts.',
            });
        } finally {
            await awaitMinimumDurationThenCloseAgenticExportProgress(exportProgressOpenedAtMs);
        }
    }, [
        accordionItems,
        dataTestId,
        onArtifactExport,
        onMultiSelectExport,
        openNotification,
        selectedArtifactIds,
        showErrorNotification,
        showSuccessNotification,
    ]);

    const handleMultiSelectExportCurrentViewExcel = useCallback(() => {
        if (selectedArtifactIds.length === 0) return;
        const sheetSpecs: ExportTableVizSheetSpec[] = [];
        for (const id of selectedArtifactIds) {
            const found = findAgenticItemById(accordionItems, id);
            if (!found?.content || found.content.type !== 'chartVisualization') {
                continue;
            }
            const viz = extractTableVizFromChartVisualizationContent(found.content);
            if (viz) {
                sheetSpecs.push({ viz, title: found.title });
            }
        }
        if (sheetSpecs.length === 0) {
            showErrorNotification({
                message: 'Export failed',
                description: 'None of the selected artefacts include an exportable table view.',
            });
            return;
        }
        void (async () => {
            openAgenticExportProgressNotification(openNotification, 'xlsx', {
                'data-test-id': `${dataTestId}-export-progress`,
            });
            const exportProgressOpenedAtMs = performance.now();
            try {
                await awaitProgressNotificationPaint();
                exportMultipleAgenticTableVizToExcel(sheetSpecs, resolvedConversationTitle);
                const skipped = selectedArtifactIds.length - sheetSpecs.length;
                showSuccessNotification({
                    message: 'Export successful',
                    description:
                        skipped > 0
                            ? `Workbook with ${sheetSpecs.length} sheet(s) exported. ${skipped} selected artefact(s) had no table view and were skipped.`
                            : `Workbook with ${sheetSpecs.length} sheet(s) exported (current table views).`,
                });
            } catch (err) {
                showErrorNotification({
                    message: 'Export failed',
                    description: err instanceof Error ? err.message : 'Could not export table views.',
                });
            } finally {
                await awaitMinimumDurationThenCloseAgenticExportProgress(exportProgressOpenedAtMs);
            }
        })();
    }, [
        accordionItems,
        dataTestId,
        openNotification,
        resolvedConversationTitle,
        selectedArtifactIds,
        showErrorNotification,
        showSuccessNotification,
    ]);

    const handleMultiSelectExport = useCallback(
        async (format: 'docx' | 'pdf' | 'ppt') => {
            if (selectedArtifactIds.length === 0) return;

            if (!contentExportRef.current) return;

            const selectedElements = selectedArtifactIds
                .map((id) => getTaskExportElement(id))
                .filter((el): el is HTMLElement => el !== null);

            if (selectedElements.length === 0) return;

            openAgenticExportProgressNotification(openNotification, format, {
                'data-test-id': `${dataTestId}-export-progress`,
            });
            const exportProgressOpenedAtMs = performance.now();
            const multiBasename = buildAskAsperExportBasename(resolvedConversationTitle);
            const ext = format === 'docx' ? 'docx' : format === 'pdf' ? 'pdf' : 'pptx';

            try {
                await withChartExportMode(async () => {
                    if (format === 'pdf') {
                        await exportMultipleElementsToPDF(selectedElements, {
                            title: resolvedConversationTitle,
                            filename: `${multiBasename}.${ext}`,
                        });
                    } else if (format === 'docx') {
                        await exportMultipleElementsToDOCX(selectedElements, {
                            title: resolvedConversationTitle,
                            filename: `${multiBasename}.${ext}`,
                        });
                    } else if (format === 'ppt') {
                        await exportMultipleElementsToPPTX(selectedElements, {
                            title: resolvedConversationTitle,
                            filename: `${multiBasename}.${ext}`,
                            coverSlide: {
                                threadTitle: resolvedConversationTitle,
                                downloadedAt: new Date(),
                            },
                        });
                    }
                });
                showSuccessNotification({
                    message: 'Export successful',
                    description: `${selectedArtifactIds.length} artifact(s) exported as ${format.toUpperCase()} successfully.`,
                });
                onMultiSelectExport?.(format, selectedArtifactIds);
            } catch (err) {
                showErrorNotification({
                    message: 'Export failed',
                    description: err instanceof Error ? err.message : 'Failed to export selected artifacts.',
                });
            } finally {
                await awaitMinimumDurationThenCloseAgenticExportProgress(exportProgressOpenedAtMs);
            }
        },
        [
            getTaskExportElement,
            onMultiSelectExport,
            resolvedConversationTitle,
            selectedArtifactIds,
            showSuccessNotification,
            showErrorNotification,
            openNotification,
            dataTestId,
        ],
    );

    const multiSelectExportMenuItems: AskAsperAccordionMenuItem[] = React.useMemo(() => {
        const hasTableCurrentViewAmongSelected = selectedArtifactIds.some((id) => {
            const found = findAgenticItemById(accordionItems, id);
            if (!found?.content || found.content.type !== 'chartVisualization') {
                return false;
            }
            return extractTableVizFromChartVisualizationContent(found.content) != null;
        });

        const items: AskAsperAccordionMenuItem[] = [
            {
                key: 'export-word',
                label: 'Export as Word',
                icon: <DOCX size={16} />,
                onClick: () => {
                    onMultiSelectExportOptionClick?.('docx');
                    handleMultiSelectExport('docx');
                },
            },
            {
                key: 'export-pdf',
                label: 'Export as PDF',
                icon: <PDF size={16} />,
                onClick: () => {
                    onMultiSelectExportOptionClick?.('pdf');
                    handleMultiSelectExport('pdf');
                },
            },
            {
                key: 'export-ppt',
                label: 'Export as PPT',
                icon: <PPT size={16} />,
                onClick: () => {
                    onMultiSelectExportOptionClick?.('ppt');
                    handleMultiSelectExport('ppt');
                },
            },
            {
                key: 'export-excel',
                label: 'Export as Excel',
                icon: <Excel size={16} />,
                submenuPlacement: 'left',
                children: [
                    {
                        key: 'export-excel-complete',
                        label: 'Export complete dataset',
                        disabled: !hasTableCurrentViewAmongSelected,
                        onClick: () => {
                            onMultiSelectExportOptionClick?.('xlsx_complete_dataset');
                            void handleMultiSelectExportCompleteDatasetXlsx();
                        },
                    },
                    {
                        key: 'export-excel-current',
                        label: 'Export current view',
                        disabled: !hasTableCurrentViewAmongSelected,
                        onClick: () => {
                            onMultiSelectExportOptionClick?.('xlsx_current_view');
                            handleMultiSelectExportCurrentViewExcel();
                        },
                    },
                ],
            },
        ];
        return items;
    }, [
        accordionItems,
        handleMultiSelectExport,
        handleMultiSelectExportCompleteDatasetXlsx,
        handleMultiSelectExportCurrentViewExcel,
        onMultiSelectExportOptionClick,
        selectedArtifactIds,
    ]);

    // Get active artifact or first artifact if none selected
    const activeArtifact = activeArtifactId ? artifacts.find((a) => a.id === activeArtifactId) : artifacts[0];

    // Helper function to get thumbnail border class
    const getThumbnailClass = (artifactId: string) => {
        if (artifactId === activeArtifactId) {
            return 'border-[#7C3AED] bg-[#F3E8FF]';
        }
        if (selectedArtifactIds.includes(artifactId)) {
            return 'border-[#5f49bc] bg-[#fbfaff]';
        }
        return 'border-[rgba(0,0,0,0.06)] bg-white hover:border-[rgba(0,0,0,0.12)]';
    };

    // Check if we have accordion items to render
    const hasAccordionItems = accordionItems.length > 0;

    const scrollPanelToBottom = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || userHasScrolledUpRef.current) return;
        isProgrammaticScrollRef.current = true;
        lastProgrammaticScrollRef.current = Date.now();
        container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
        requestAnimationFrame(() => {
            isProgrammaticScrollRef.current = false;
        });
    }, []);

    const contentScrollKey = useMemo(() => {
        const itemIds = accordionItems.flatMap((item) =>
            item.children?.length ? item.children.map((child) => child.id) : [item.id],
        );
        const summaryLoadingBlocks = rightPanelBlocks?.filter((block) => block.summaryLoading).length ?? 0;
        return [itemIds.join(','), summaries.length, summaryLoadingBlocks, artifacts.length].join('|');
    }, [accordionItems, summaries.length, rightPanelBlocks, artifacts.length]);

    /** Detect when user manually scrolls up - disable auto-scroll until next question */
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || isProgrammaticScrollRef.current) return;
        if (Date.now() - lastProgrammaticScrollRef.current < 150) return;
        const { scrollTop, clientHeight, scrollHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        if (distanceFromBottom > SCROLL_NEAR_BOTTOM_THRESHOLD) {
            userHasScrolledUpRef.current = true;
        }
    }, []);

    // Auto-scroll when artefact/summary content changes. Skip when only pair count rose (new
    // question) and right-panel artefact ids are unchanged — avoids jumping into empty filler.
    useEffect(() => {
        const pairCount = conversationPairCount ?? 0;
        const pairCountIncreased = pairCount > prevPairCountRef.current;
        prevPairCountRef.current = pairCount;

        if (pairCountIncreased) {
            userHasScrolledUpRef.current = false;
        }

        const contentChanged = contentScrollKey !== prevContentScrollKeyRef.current;
        prevContentScrollKeyRef.current = contentScrollKey;

        if (pairCountIncreased && !contentChanged) {
            return;
        }

        if (contentChanged) {
            requestAnimationFrame(scrollPanelToBottom);
        }
    }, [contentScrollKey, conversationPairCount, scrollPanelToBottom]);

    // Keep pinned to bottom while charts/tables grow in-place during streaming (same artefact ids).
    useEffect(() => {
        const content = contentExportRef.current;
        if (!content) return;

        const observer = new ResizeObserver(() => {
            requestAnimationFrame(scrollPanelToBottom);
        });
        observer.observe(content);
        return () => observer.disconnect();
    }, [scrollPanelToBottom, hasAccordionItems, summaries.length]);

    /** Expand target artefact and scroll into view when opened via export deep link. */
    useEffect(() => {
        if (!deepLinkTaskId) return;

        userHasScrolledUpRef.current = true;

        return scrollAgenticRightPanelToTask(scrollContainerRef.current, deepLinkTaskId, (found) => {
            lastProgrammaticScrollRef.current = Date.now();
            onDeepLinkTaskScrollComplete?.(deepLinkTaskId, found);
        });
    }, [deepLinkTaskId, accordionItems, onDeepLinkTaskScrollComplete]);

    /**
     * Expand the cited artefact and scroll it into view when a summary citation badge is clicked.
     * `nonce` makes repeat clicks on the same badge re-scroll.
     */
    const [citationTarget, setCitationTarget] = useState<{ taskId: string; nonce: number } | undefined>(undefined);

    const handleSummaryCitationClick = useCallback(
        (taskId: string, serial: number) => {
            setCitationTarget((prev) => ({ taskId, nonce: (prev?.nonce ?? 0) + 1 }));
            onSummaryCitationClick?.(taskId, serial);
        },
        [onSummaryCitationClick],
    );

    useEffect(() => {
        if (!citationTarget) return;

        userHasScrolledUpRef.current = true;

        return scrollAgenticRightPanelToTask(scrollContainerRef.current, citationTarget.taskId, () => {
            lastProgrammaticScrollRef.current = Date.now();
        });
    }, [citationTarget]);

    /** Artefact to keep expanded: the cited one, else the export deep-link target. */
    const focusTaskId = citationTarget?.taskId ?? deepLinkTaskId;

    /** Scroll the matching question response block into view when opened via a runId deep link. */
    useEffect(() => {
        if (!deepLinkRunId) return;

        userHasScrolledUpRef.current = true;

        return scrollAgenticRightPanelToRun(scrollContainerRef.current, deepLinkRunId, (found) => {
            lastProgrammaticScrollRef.current = Date.now();
            onDeepLinkRunScrollComplete?.(deepLinkRunId, found);
        });
    }, [deepLinkRunId, accordionItems, onDeepLinkRunScrollComplete]);

    return (
        <ConfigProvider theme={theme}>
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex flex-col h-full min-w-0 bg-[#FAFAFA] overflow-y-auto overflow-x-hidden rounded-[12px]"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-right-panel
            >
                {/* Header */}
                <div className="bg-white sticky top-0 z-10 rounded-t-lg">
                    {/* Title Row */}
                    <div className="px-4 pt-4 pb-2">
                        <div className="flex items-center justify-between">
                            <h2 className="font-sans font-semibold text-[16px] leading-normal text-[#18181b]">
                                Artefacts
                            </h2>
                            <div className="flex items-center gap-2.5">
                                {/* Focus Mode Toggle - shown when navbar is disabled */}
                                {showFocusModeToggleInPanel && onViewModeChangeInPanel && (
                                    <div className="bg-[#f4f4f5] flex items-center p-0.5 rounded-md">
                                        <Tooltip
                                            title={viewMode === 'list' ? 'List view' : 'Return to list view'}
                                            placement="bottom"
                                            getPopupContainer={() => document.body}
                                        >
                                            <button
                                                type="button"
                                                className={`flex items-center justify-center w-7 h-7 rounded cursor-pointer transition-all ${
                                                    viewMode === 'list' ? 'bg-white shadow-sm' : ''
                                                }`}
                                                onClick={() => onViewModeChangeInPanel('list')}
                                                aria-label={viewMode === 'list' ? 'List view' : 'Return to list view'}
                                                data-test-id={`${dataTestId}-focus-mode-list`}
                                            >
                                                <Menu
                                                    size={20}
                                                    color={
                                                        viewMode === 'list' ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.45)'
                                                    }
                                                />
                                            </button>
                                        </Tooltip>
                                        <Tooltip
                                            title={
                                                viewMode === 'presentation'
                                                    ? 'Presentation view'
                                                    : 'Switch to presentation view'
                                            }
                                            placement="bottom"
                                            getPopupContainer={() => document.body}
                                        >
                                            <button
                                                type="button"
                                                className={`flex items-center justify-center w-7 h-7 rounded cursor-pointer transition-all ${
                                                    viewMode === 'presentation' ? 'bg-white shadow-sm' : ''
                                                }`}
                                                onClick={() => onViewModeChangeInPanel('presentation')}
                                                aria-label={
                                                    viewMode === 'presentation'
                                                        ? 'Presentation view'
                                                        : 'Switch to presentation view'
                                                }
                                                data-test-id={`${dataTestId}-focus-mode-presentation`}
                                            >
                                                <Stand
                                                    size={20}
                                                    color={
                                                        viewMode === 'presentation'
                                                            ? 'rgba(0,0,0,0.88)'
                                                            : 'rgba(0,0,0,0.45)'
                                                    }
                                                />
                                            </button>
                                        </Tooltip>
                                    </div>
                                )}
                                {/* Select Multiple Button - Always visible and enabled when accordion items are present */}
                                {hasAccordionItems && (
                                    <Tooltip
                                        title="Select multiple artifacts for bulk actions."
                                        placement="bottom"
                                        getPopupContainer={() => document.body}
                                    >
                                        <button
                                            type="button"
                                            className="flex items-center gap-2 h-8 px-4 bg-transparent border-none cursor-pointer hover:bg-[rgba(0,0,0,0.04)] rounded-md transition-colors"
                                            onClick={onToggleMultiSelect || (() => {})}
                                            data-test-id={`${dataTestId}-select-multiple`}
                                            aria-label="Select multiple artifacts for bulk actions."
                                        >
                                            <CheckDone
                                                size={20}
                                                color={isMultiSelectMode ? '#5f49bc' : 'rgba(0,0,0,0.88)'}
                                            />
                                            <span
                                                className={`font-['Open_Sans'] font-medium text-[14px] leading-[22px] ${
                                                    isMultiSelectMode ? 'text-[#5f49bc]' : 'text-[rgba(0,0,0,0.88)]'
                                                }`}
                                            >
                                                Select Multiple
                                            </span>
                                        </button>
                                    </Tooltip>
                                )}
                                {/* Close Button */}
                                {onClose && (
                                    <button
                                        type="button"
                                        className="flex items-center justify-center size-6 bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity p-0"
                                        onClick={onClose}
                                        aria-label="Close artefacts panel"
                                        data-test-id={`${dataTestId}-close`}
                                    >
                                        <XClose size={24} color="rgba(0,0,0,0.45)" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-[rgba(0,0,0,0.06)] w-full" />

                    {/* Selection Strip - Shows when multi-select mode is active */}
                    {isMultiSelectMode && (
                        <div className="bg-[#f7f5ff] px-3 py-2 w-full" data-test-id={`${dataTestId}-selection-strip`}>
                            <div className="flex items-center justify-between w-full">
                                {/* Left side - Selection info and actions */}
                                <div className="flex items-center gap-2.5">
                                    <p className="font-sans text-[14px] leading-normal">
                                        <span className="font-medium text-[#5f49bc]">
                                            {selectedArtifactIds.length}{' '}
                                        </span>
                                        <span className="font-normal text-[#18181b]">Items selected</span>
                                    </p>
                                    <div className="flex items-center gap-0.5">
                                        <button
                                            type="button"
                                            className="px-2 h-6 flex items-center justify-center bg-transparent border-none cursor-pointer rounded hover:bg-[rgba(95,73,188,0.1)] transition-colors"
                                            onClick={() => {
                                                // Select all accordion items (including nested children)
                                                const allItemIds: string[] = [];
                                                accordionItems.forEach((item) => {
                                                    // Add parent item if it has content
                                                    if (item.content && !selectedArtifactIds.includes(item.id)) {
                                                        allItemIds.push(item.id);
                                                    }
                                                    // Add all child items
                                                    if (item.children && item.children.length > 0) {
                                                        item.children.forEach((child) => {
                                                            if (!selectedArtifactIds.includes(child.id)) {
                                                                allItemIds.push(child.id);
                                                            }
                                                        });
                                                    }
                                                });
                                                // Select all items
                                                allItemIds.forEach((id) => {
                                                    onArtifactSelectionChange?.(id, true);
                                                });
                                                onSelectAll?.(allItemIds);
                                            }}
                                            data-test-id={`${dataTestId}-select-all`}
                                        >
                                            <span className="font-sans font-medium text-sm leading-[20px] text-[#5f49bc] underline">
                                                Select all
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`px-2 h-6 flex items-center justify-center bg-transparent border-none cursor-pointer rounded hover:bg-[rgba(95,73,188,0.1)] transition-colors ${
                                                selectedArtifactIds.length === 0 ? 'opacity-50' : ''
                                            }`}
                                            onClick={() => {
                                                // Deselect all
                                                const cleared = [...selectedArtifactIds];
                                                cleared.forEach((id) => {
                                                    onArtifactSelectionChange?.(id, false);
                                                });
                                                onDeselectAll?.(cleared);
                                            }}
                                            disabled={selectedArtifactIds.length === 0}
                                            data-test-id={`${dataTestId}-deselect-all`}
                                        >
                                            <span className="font-sans font-medium text-sm leading-[20px] text-[#5f49bc] underline">
                                                Deselect all
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Right side — primary Export (Ask Asper 3.0 selection strip) */}
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <AskAsperAccordionMenu items={multiSelectExportMenuItems} placement="bottomRight">
                                        <button
                                            type="button"
                                            className="flex items-center justify-center gap-2 min-h-6 px-[7px] rounded border-0 bg-[#44338a] text-white text-xs font-semibold leading-[22px] font-sans shadow-[0px_2px_0px_0px_rgba(95,73,188,0.1)] cursor-pointer hover:bg-[#3b2d77] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#44338a]"
                                            disabled={selectedArtifactIds.length === 0}
                                            aria-label="Export selected"
                                            data-test-id={`${dataTestId}-export-selected`}
                                        >
                                            <DownloadFile size={14} color="#FFFFFF" className="shrink-0" />
                                            <span className="whitespace-nowrap">Export</span>
                                        </button>
                                    </AskAsperAccordionMenu>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Content - White background for summaries + artifacts (ref for PDF/Word export) */}
                <div ref={contentExportRef} className="min-w-0 px-4 md:px-6 py-4 md:py-6 space-y-4 bg-white">
                    {/* When rightPanelBlocks: ordered per message (Q1 summary+artifacts, Q2 summary+artifacts, ...) */}
                    {rightPanelBlocks != null && rightPanelBlocks.length > 0
                        ? rightPanelBlocks.map((block, blockIdx) => {
                              const blockHasParentAccordion = block.accordionItems.some(
                                  (item) => item.children != null && item.children.length > 0,
                              );
                              const showSummaryLoaderFallback = block.summaryLoading && !blockHasParentAccordion;
                              const blockRunId =
                                  block.runId ?? block.accordionItems.find((item) => item.run_id)?.run_id;
                              const blockRunDeepLinkUrl =
                                  exportDeepLinkContext?.config && exportDeepLinkContext.threadId && blockRunId
                                      ? buildAgenticExportDeepLinkUrl(
                                            exportDeepLinkContext.config,
                                            exportDeepLinkContext.threadId,
                                            { runId: blockRunId },
                                        )
                                      : undefined;
                              return (
                                  <div
                                      key={blockIdx}
                                      className="flex flex-col gap-4"
                                      data-right-panel-block
                                      data-run-id={blockRunId}
                                      data-user-query={
                                          block.userQuery != null && block.userQuery !== ''
                                              ? encodeURIComponent(block.userQuery)
                                              : undefined
                                      }
                                      data-message-index={block.messageIndex}
                                  >
                                      {block.summary && (
                                          <div data-export-slide data-export-slide-title="Analysis Summary">
                                              <AgenticAnalysisSummary
                                                  summary={block.summary}
                                                  data-test-id={`${dataTestId}-summary-${block.messageIndex}`}
                                              />
                                          </div>
                                      )}
                                      {block.accordionItems
                                          .filter(
                                              (item) =>
                                                  item.content?.type !== 'analysisSummary' ||
                                                  hasAnalysisSummaryContent(item.content as AnalysisSummaryContent),
                                          )
                                          .map((item) => {
                                              if (item.children && item.children.length > 0) {
                                                  const parentAccordionTestId = `${dataTestId}-parent-accordion-${item.id}`;
                                                  const { allSelected, someSelected } = getChildArtifactSelectionState(
                                                      item.children,
                                                      selectedArtifactIds,
                                                  );
                                                  return (
                                                      <div
                                                          key={item.id}
                                                          data-task-id={item.id}
                                                          data-export-slide-title={item.title}
                                                          data-export-content
                                                          data-export-slide
                                                          className="w-full"
                                                      >
                                                          <ParentAccordion
                                                              title={item.title}
                                                              index={item.index}
                                                              defaultExpanded={item.defaultExpanded ?? true}
                                                              expanded={
                                                                  accordionShouldForceExpandForDeepLink(item, focusTaskId)
                                                                      ? true
                                                                      : undefined
                                                              }
                                                              dataTestId={parentAccordionTestId}
                                                              showCheckbox={isMultiSelectMode}
                                                              bulkChecked={allSelected}
                                                              bulkIndeterminate={someSelected}
                                                              onBulkCheckChange={(checked) => {
                                                                  item.children!.forEach((child) => {
                                                                      onArtifactSelectionChange?.(child.id, checked);
                                                                  });
                                                              }}
                                                          >
                                                              {block.summaryLoading && (
                                                                  <AgenticAnalysisSummaryLoading
                                                                      data-test-id={`${dataTestId}-summary-loading-${block.messageIndex}`}
                                                                  />
                                                              )}
                                                              {item.children.map((childItem) =>
                                                                  renderChildAccordionItem(
                                                                      childItem,
                                                                      isMultiSelectMode,
                                                                      selectedArtifactIds,
                                                                      onArtifactSelectionChange,
                                                                      parentAccordionTestId,
                                                                      getMenuItemsForItem,
                                                                      item.title,
                                                                      onMethodologyExpandChange,
                                                                      onArtefactMenuTriggerClick,
                                                                      onTrackChartEvent,
                                                                      exportDeepLinkContext,
                                                                      focusTaskId,
                                                                      handleSummaryCitationClick,
                                                                  ),
                                                              )}
                                                          </ParentAccordion>
                                                      </div>
                                                  );
                                              }
                                              return (
                                                  <div
                                                      key={item.id}
                                                      data-task-id={item.id}
                                                      data-export-slide-title={item.title}
                                                      className="w-full"
                                                  >
                                                      <AskAsperAccordion
                                                          title={formatTaskTitleWithSerial(item.title, item.taskSerial)}
                                                          defaultExpanded={item.defaultExpanded ?? true}
                                                          expanded={focusTaskId === item.id ? true : undefined}
                                                          showCheckbox={isMultiSelectMode}
                                                          checked={selectedArtifactIds.includes(item.id)}
                                                          onCheckChange={(isChecked: boolean) =>
                                                              onArtifactSelectionChange?.(item.id, isChecked)
                                                          }
                                                          initialFeedbackVote={item.initialFeedbackVote}
                                                          onThumbsDown={item.onThumbsDown}
                                                          onThumbsUp={item.onThumbsUp}
                                                          onDislikeFeedbackSubmit={toAccordionDislikeFeedbackSubmit(item.onDislikeFeedbackSubmit)}
                                                          dislikeFeedbackContextTitle={item.dislikeFeedbackContextTitle}
                                                          menuItems={getMenuItemsForItem(item)}
                                                          menuTriggerIcon="export"
                                                          onMenuTriggerClick={
                                                              onArtefactMenuTriggerClick
                                                                  ? () => onArtefactMenuTriggerClick(item.title)
                                                                  : undefined
                                                          }
                                                          data-test-id={`${dataTestId}-accordion-${item.id}`}
                                                      >
                                                          <div
                                                              data-export-content
                                                              data-export-slide
                                                              data-export-slide-title={item.title}
                                                              className="w-full"
                                                          >
                                                              {renderAccordionContent(
                                                                  item.content!,
                                                                  `${dataTestId}-accordion-${item.id}`,
                                                                  onMethodologyExpandChange,
                                                                  item.title,
                                                                  onTrackChartEvent,
                                                                  exportDeepLinkContext
                                                                      ? { ...exportDeepLinkContext, taskId: item.id }
                                                                      : undefined,
                                                                  handleSummaryCitationClick,
                                                              )}
                                                          </div>
                                                      </AskAsperAccordion>
                                                  </div>
                                              );
                                          })}
                                      {showSummaryLoaderFallback && (
                                          <AgenticAnalysisSummaryLoading
                                              data-test-id={`${dataTestId}-summary-loading-${block.messageIndex}`}
                                          />
                                      )}
                                      {blockRunDeepLinkUrl && (
                                          <div
                                              data-export-exclude
                                              data-export-run-deeplink
                                              className="sr-only"
                                              data-test-id={`${dataTestId}-run-deeplink-${block.messageIndex}`}
                                          >
                                              <span
                                                  data-export-run-deeplink-url={blockRunDeepLinkUrl}
                                                  aria-hidden
                                              >
                                                  Open in AskAsper
                                              </span>
                                          </div>
                                      )}
                                  </div>
                              );
                          })
                        : null}

                    {/* Legacy: summaries then accordion items (when rightPanelBlocks not provided) */}
                    {rightPanelBlocks == null && summaries.length > 0 && (
                        <div className="flex flex-col gap-4">
                            {summaries.map((s, i) => (
                                <div key={i} data-export-slide data-export-slide-title="Analysis Summary">
                                    <AgenticAnalysisSummary summary={s} data-test-id={`${dataTestId}-summary-${i}`} />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Accordion Items - Data Driven (legacy or when no rightPanelBlocks) */}
                    {rightPanelBlocks == null &&
                        hasAccordionItems &&
                        accordionItems
                            .filter(
                                (item) =>
                                    item.content?.type !== 'analysisSummary' ||
                                    hasAnalysisSummaryContent(item.content as AnalysisSummaryContent),
                            )
                            .map((item) => {
                                // Check if this is a group accordion (has children)
                                if (item.children && item.children.length > 0) {
                                    const parentAccordionTestId = `${dataTestId}-parent-accordion-${item.id}`;
                                    const { allSelected, someSelected } = getChildArtifactSelectionState(
                                        item.children,
                                        selectedArtifactIds,
                                    );
                                    return (
                                        <div
                                            key={item.id}
                                            data-task-id={item.id}
                                            data-export-slide-title={item.title}
                                            data-export-content
                                            data-export-slide
                                            className="w-full"
                                        >
                                            <ParentAccordion
                                                title={item.title}
                                                index={item.index}
                                                defaultExpanded={item.defaultExpanded ?? true}
                                                expanded={
                                                    accordionShouldForceExpandForDeepLink(item, focusTaskId)
                                                        ? true
                                                        : undefined
                                                }
                                                dataTestId={parentAccordionTestId}
                                                showCheckbox={isMultiSelectMode}
                                                bulkChecked={allSelected}
                                                bulkIndeterminate={someSelected}
                                                onBulkCheckChange={(checked) => {
                                                    item.children!.forEach((child) => {
                                                        onArtifactSelectionChange?.(child.id, checked);
                                                    });
                                                }}
                                            >
                                                {item.children.map((childItem) =>
                                                    renderChildAccordionItem(
                                                        childItem,
                                                        isMultiSelectMode,
                                                        selectedArtifactIds,
                                                        onArtifactSelectionChange,
                                                        parentAccordionTestId,
                                                        getMenuItemsForItem,
                                                        item.title,
                                                        onMethodologyExpandChange,
                                                        onArtefactMenuTriggerClick,
                                                        onTrackChartEvent,
                                                        exportDeepLinkContext,
                                                        focusTaskId,
                                                        handleSummaryCitationClick,
                                                    ),
                                                )}
                                            </ParentAccordion>
                                        </div>
                                    );
                                }

                                // Regular accordion item with content
                                return (
                                    <div
                                        key={item.id}
                                        data-task-id={item.id}
                                        data-export-slide-title={item.title}
                                        className="w-full"
                                    >
                                        <AskAsperAccordion
                                            title={formatTaskTitleWithSerial(item.title, item.taskSerial)}
                                            defaultExpanded={item.defaultExpanded ?? true}
                                            expanded={focusTaskId === item.id ? true : undefined}
                                            showCheckbox={isMultiSelectMode}
                                            checked={selectedArtifactIds.includes(item.id)}
                                            onCheckChange={(isChecked: boolean) =>
                                                onArtifactSelectionChange?.(item.id, isChecked)
                                            }
                                            initialFeedbackVote={item.initialFeedbackVote}
                                            onThumbsDown={item.onThumbsDown}
                                            onThumbsUp={item.onThumbsUp}
                                            onDislikeFeedbackSubmit={toAccordionDislikeFeedbackSubmit(item.onDislikeFeedbackSubmit)}
                                            dislikeFeedbackContextTitle={item.dislikeFeedbackContextTitle}
                                            menuItems={getMenuItemsForItem(item)}
                                            menuTriggerIcon="export"
                                            onMenuTriggerClick={
                                                onArtefactMenuTriggerClick
                                                    ? () => onArtefactMenuTriggerClick(item.title)
                                                    : undefined
                                            }
                                            data-test-id={`${dataTestId}-accordion-${item.id}`}
                                        >
                                            <div
                                                data-export-content
                                                data-export-slide
                                                data-export-slide-title={item.title}
                                                className="w-full"
                                            >
                                                {renderAccordionContent(
                                                    item.content,
                                                    `${dataTestId}-accordion-${item.id}`,
                                                    onMethodologyExpandChange,
                                                    item.title,
                                                    onTrackChartEvent,
                                                    exportDeepLinkContext
                                                        ? { ...exportDeepLinkContext, taskId: item.id }
                                                        : undefined,
                                                    handleSummaryCitationClick,
                                                )}
                                            </div>
                                        </AskAsperAccordion>
                                    </div>
                                );
                            })}

                    {/* Legacy: Active Artifact (for backward compatibility) */}
                    {!hasAccordionItems && activeArtifact && (
                        <AgenticArtifactCard
                            artifact={{
                                ...activeArtifact,
                                onClose: () => {
                                    if (onArtifactClose) {
                                        onArtifactClose(activeArtifact.id);
                                    }
                                    if (activeArtifact.onClose) {
                                        activeArtifact.onClose();
                                    }
                                },
                            }}
                            showCheckbox={isMultiSelectMode}
                            checked={selectedArtifactIds.includes(activeArtifact.id)}
                            onCheckChange={(isChecked: boolean) =>
                                onArtifactSelectionChange?.(activeArtifact.id, isChecked)
                            }
                            data-test-id={`${dataTestId}-active-artifact`}
                        />
                    )}

                    {/* Artifact Navigation Thumbnails (if multiple artifacts and no accordion items) */}
                    {!hasAccordionItems && artifacts.length > 1 && (
                        <div className="flex items-center gap-2 overflow-x-auto pb-2">
                            <span className="font-['Open_Sans'] text-sm leading-[18px] text-[rgba(0,0,0,0.45)] whitespace-nowrap">
                                ← Artefact →
                            </span>
                            {artifacts.map((artifact) => (
                                <button
                                    key={artifact.id}
                                    onClick={() => onArtifactSelect?.(artifact.id)}
                                    className={`flex-shrink-0 w-24 h-16 rounded border-2 transition-all ${getThumbnailClass(artifact.id)}`}
                                    data-test-id={`${dataTestId}-artifact-thumbnail-${artifact.id}`}
                                >
                                    <div className="p-2 text-[10px] font-['Open_Sans'] text-[rgba(0,0,0,0.65)] truncate">
                                        {artifact.title}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {accordionItems.length === 0 && artifacts.length === 0 && summaries.length === 0 && (
                        <div className="flex flex-col items-center justify-center min-h-[480px] gap-3 py-8">
                            {/* Illustration */}
                            <div className="relative w-[364px] h-[280px]">
                                {/* Grid Background */}
                                <div className="absolute inset-0 opacity-30">
                                    <svg width="100%" height="100%" viewBox="0 0 364 280">
                                        <defs>
                                            <pattern
                                                id="emptyStateGrid"
                                                width="40"
                                                height="40"
                                                patternUnits="userSpaceOnUse"
                                            >
                                                <path
                                                    d="M 40 0 L 0 0 0 40"
                                                    fill="none"
                                                    stroke="#e4e4e7"
                                                    strokeWidth="1"
                                                />
                                            </pattern>
                                        </defs>
                                        <rect width="100%" height="100%" fill="url(#emptyStateGrid)" />
                                    </svg>
                                </div>

                                {/* Placeholder Cards */}
                                {/* Top card */}
                                <div
                                    className="absolute left-[77px] top-[60px] w-[213px] h-[46px] rounded-md border-[1.5px] border-white"
                                    style={{
                                        background:
                                            'linear-gradient(180deg, rgb(235, 236, 240) 11%, rgb(255, 255, 255) 99%)',
                                    }}
                                >
                                    <div className="absolute left-[7px] top-[7px] w-[30px] h-[30px] bg-white border border-[#f0f0f0] rounded-md flex items-center justify-center">
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M7 8.5H12M7 12H15M7 18V20.3355C7 20.8684 7 21.1348 7.10923 21.2716C7.20422 21.3906 7.34827 21.4599 7.50054 21.4597C7.67563 21.4595 7.88367 21.2931 8.29976 20.9602L10.6852 19.0518C11.1725 18.662 11.4162 18.4671 11.6875 18.3285C11.9282 18.2055 12.1844 18.1156 12.4492 18.0613C12.7477 18 13.0597 18 13.6837 18H16.2C17.8802 18 18.7202 18 19.362 17.673C19.9265 17.3854 20.3854 16.9265 20.673 16.362C21 15.7202 21 14.8802 21 13.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V14C3 14.93 3 15.395 3.10222 15.7765C3.37962 16.8117 4.18827 17.6204 5.22354 17.8978C5.60504 18 6.07003 18 7 18Z"
                                                stroke="#9ca3af"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <div className="absolute left-[42px] top-[10px] flex flex-col gap-[5px]">
                                        <div className="w-[162px] h-[8px] bg-[#dbdfe3] rounded-full" />
                                        <div className="w-[72px] h-[8px] bg-[#e3e4ea] rounded-full" />
                                    </div>
                                </div>

                                {/* Middle card (highlighted) */}
                                <div
                                    className="absolute left-[53px] top-[100px] w-[265px] h-[58px] rounded-md border-[1.5px] border-white shadow-lg"
                                    style={{
                                        background:
                                            'linear-gradient(180deg, rgb(235, 236, 240) 11%, rgb(255, 255, 255) 54%, rgb(255, 255, 255) 99%)',
                                    }}
                                >
                                    <div className="absolute left-[7px] top-[12px] w-[30px] h-[30px] bg-white border border-[#f0f0f0] rounded-md flex items-center justify-center">
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M7 8.5H12M7 12H15M7 18V20.3355C7 20.8684 7 21.1348 7.10923 21.2716C7.20422 21.3906 7.34827 21.4599 7.50054 21.4597C7.67563 21.4595 7.88367 21.2931 8.29976 20.9602L10.6852 19.0518C11.1725 18.662 11.4162 18.4671 11.6875 18.3285C11.9282 18.2055 12.1844 18.1156 12.4492 18.0613C12.7477 18 13.0597 18 13.6837 18H16.2C17.8802 18 18.7202 18 19.362 17.673C19.9265 17.3854 20.3854 16.9265 20.673 16.362C21 15.7202 21 14.8802 21 13.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V14C3 14.93 3 15.395 3.10222 15.7765C3.37962 16.8117 4.18827 17.6204 5.22354 17.8978C5.60504 18 6.07003 18 7 18Z"
                                                stroke="#9ca3af"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <div className="absolute left-[42px] top-[16px] flex flex-col gap-[5px]">
                                        <div className="w-[162px] h-[8px] bg-[#dbdfe3] rounded-full" />
                                        <div className="w-[72px] h-[8px] bg-[#e3e4ea] rounded-full" />
                                    </div>
                                </div>

                                {/* Bottom card */}
                                <div
                                    className="absolute left-[77px] top-[152px] w-[213px] h-[46px] rounded-md border-[1.5px] border-white"
                                    style={{
                                        background:
                                            'linear-gradient(180deg, rgb(235, 236, 240) 11%, rgb(255, 255, 255) 99%)',
                                    }}
                                >
                                    <div className="absolute left-[7px] top-[7px] w-[30px] h-[30px] bg-white border border-[#f0f0f0] rounded-md flex items-center justify-center">
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M7 8.5H12M7 12H15M7 18V20.3355C7 20.8684 7 21.1348 7.10923 21.2716C7.20422 21.3906 7.34827 21.4599 7.50054 21.4597C7.67563 21.4595 7.88367 21.2931 8.29976 20.9602L10.6852 19.0518C11.1725 18.662 11.4162 18.4671 11.6875 18.3285C11.9282 18.2055 12.1844 18.1156 12.4492 18.0613C12.7477 18 13.0597 18 13.6837 18H16.2C17.8802 18 18.7202 18 19.362 17.673C19.9265 17.3854 20.3854 16.9265 20.673 16.362C21 15.7202 21 14.8802 21 13.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V14C3 14.93 3 15.395 3.10222 15.7765C3.37962 16.8117 4.18827 17.6204 5.22354 17.8978C5.60504 18 6.07003 18 7 18Z"
                                                stroke="#9ca3af"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <div className="absolute left-[42px] top-[10px] flex flex-col gap-[5px]">
                                        <div className="w-[162px] h-[8px] bg-[#dbdfe3] rounded-full" />
                                        <div className="w-[72px] h-[8px] bg-[#e3e4ea] rounded-full" />
                                    </div>
                                </div>
                            </div>

                            {/* Text Content */}
                            <div className="flex flex-col items-center gap-1 text-center">
                                <h3 className="font-sans font-bold text-[20px] leading-[32px] text-[#18181b]">
                                    Artefacts will appear here as analysis will run
                                </h3>
                                <p className="font-sans text-[14px] leading-[22px] text-[#71717b]">
                                    Each phase generates artefacts
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticRightPanel;
